import { getRawCookieValue } from '../cookie-consent/parse-consent-cookie';

/**
 * A/B test: SRP job card + job detail page redesign ("brand color panel" layout).
 * Assigned once per visitor (50/50) server-side in server/vike-handler.ts and persisted via
 * cookie so every subsequent SSR render — and the CloudFront cache key, see
 * cdk/lib/vike-stack.ts's SSRCachePolicy cookie allowlist — stays consistent for that visitor.
 */
export const CARD_DESIGN_VARIANT_COOKIE_NAME = 'cardDesignVariant';

export type CardDesignVariant = 'old' | 'new';

/** ~1 year, matching other long-lived preference cookies in this app. */
export const CARD_DESIGN_VARIANT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export function isCardDesignVariant(value: unknown): value is CardDesignVariant {
  return value === 'old' || value === 'new';
}

/** Works for both a raw `Cookie` request header and `document.cookie`. */
export function readCardDesignVariantFromCookieString(
  cookieHeaderOrDocumentCookie: string | null | undefined,
): CardDesignVariant | null {
  const raw = getRawCookieValue(cookieHeaderOrDocumentCookie ?? '', CARD_DESIGN_VARIANT_COOKIE_NAME);
  return isCardDesignVariant(raw) ? raw : null;
}

/**
 * Assignment for a visitor who doesn't have the cookie yet. The 50/50 split is paused —
 * everyone gets 'new' by default until proper A/B testing tooling replaces this homegrown
 * mechanism. The 'old' design, the `?cardDesign=old` QA override, and this whole cookie
 * pipeline are left in place so the test can be resumed later without re-building anything.
 */
export function assignCardDesignVariant(): CardDesignVariant {
  return 'new';
}

/** Builds a `Set-Cookie` header value to persist a visitor's assigned variant. */
export function buildCardDesignVariantSetCookieHeader(variant: CardDesignVariant, secure: boolean): string {
  const segments = [
    `${CARD_DESIGN_VARIANT_COOKIE_NAME}=${variant}`,
    'Path=/',
    `Max-Age=${CARD_DESIGN_VARIANT_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (secure) segments.push('Secure');
  return segments.join('; ');
}
